import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {AdcValue} from '@models/adminstration/gateway/gateway-configuration/adc-value';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {HYSTERESIS_RANGE_MAX, HYSTERESIS_RANGE_MIN} from '@constants/administration/gateway/gateway-constants';

@Component({
    selector: 'scitis-admin-gateway-adc-setting',
    templateUrl: './gateway-adc-setting.component.html',
    styleUrls: ['./gateway-adc-setting.component.scss'],
    standalone: false
})
export class GatewayAdcSettingComponent implements OnInit, OnChanges {

  @Input()
  adc: AdcValue = new AdcValue();

  @Input()
  availableIndices: number[] = [];

  @Output()
  removeSettingChangeEvent = new EventEmitter<AdcValue>();

  @Output()
  changeIndexEvent = new EventEmitter<Map<string, number>>();

  @Output()
  formIsValid: EventEmitter<boolean> = new EventEmitter<boolean>();

  ioTypes = ['ANALOG', 'DIGITAL'];
  adcSettingsForm: UntypedFormGroup = new UntypedFormGroup({});
  actualIndex: number = 0;
  private ANALOG_IO_TYPE: string = 'ANALOG';

  ngOnInit(): void {
    this.generateAdcSettingsForm(this.adc);
    this.actualIndex = this.adc.index ? this.adc.index : 0;
    this.availableIndices = [...this.availableIndices, this.actualIndex];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.adc?.firstChange) {
      this.updateAdcForm();
    }
  }

  constructor() {
  }

  get formControls() {
    return this.adcSettingsForm.controls;
  }

  get HYSTERESIS_RANGE_MIN(){
    return HYSTERESIS_RANGE_MIN;
  }

  get HYSTERESIS_RANGE_MAX(){
    return HYSTERESIS_RANGE_MAX;
  }

  getAdcHeader(adcIndex: number | undefined) {
    if (adcIndex !== undefined) {
      return 'ADC ' + adcIndex;
    } else {
      return 'ADC';
    }
  }

  remove(adc: AdcValue) {
    this.removeSettingChangeEvent.emit(adc);
  }

  changeIndex(index: number) {
    const map: Map<string, number> = new Map<string, number>();
    map.set('old', this.actualIndex);
    map.set('new', index);
    this.changeIndexEvent.emit(map);
    this.actualIndex = index;
    this.adc.index = index;
  }

  onFocusOut() {
    this.onConfigUpdated();
  }

  isAnalogIOType(){
    return this.adc.ioType === this.ANALOG_IO_TYPE;
  }

  private updateAdcForm() {
    this.formControls.signalName.setValue(this.adc.name);
    this.formControls.adcIndex.setValue(this.adc.index);
    this.formControls.adcIoType.setValue(this.adc.ioType);
    this.formControls.scaleMin.setValue(this.adc.scaleMin);
    this.formControls.scaleMax.setValue(this.adc.scaleMax);
    this.formControls.adcHysteresis.setValue(this.adc.hysteresis);
  }

  private generateAdcSettingsForm(adc: AdcValue) {
    this.adcSettingsForm = new UntypedFormGroup({
      signalName: new UntypedFormControl(adc.name, [Validators.required]),
      adcIndex: new UntypedFormControl(adc.index, []),
      adcIoType: new UntypedFormControl(adc.ioType, []),
      scaleMin: new UntypedFormControl(adc.scaleMin, [Validators.required]),
      scaleMax: new UntypedFormControl(adc.scaleMax, [Validators.required]),
      adcHysteresis: new UntypedFormControl(adc.hysteresis, [Validators.required, Validators.min(HYSTERESIS_RANGE_MIN), Validators.max(HYSTERESIS_RANGE_MAX)]),
    });
    this.formIsValid.emit(!this.adcSettingsForm.invalid);
  }

  private onConfigUpdated() {
    this.adc.name = this.formControls.signalName.value;
    this.adc.index = this.formControls.adcIndex.value;
    this.adc.ioType = this.formControls.adcIoType.value;
    this.adc.scaleMin = this.formControls.scaleMin.value;
    this.adc.scaleMax = this.formControls.scaleMax.value;
    this.adc.hysteresis = this.formControls.adcHysteresis.value;
    this.formIsValid.emit(!this.adcSettingsForm.invalid);
  }

}
